import React, { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import {
  Users, Briefcase, FileText, CalendarDays,
  AlertCircle, Plus,
} from 'lucide-react'
import { format, isToday, addDays, isAfter } from 'date-fns'

function fmtDate(dateStr) {
  if (!dateStr) return '—'
  try { return format(new Date(dateStr), 'MMM d, yyyy') } catch { return '—' }
}

const LEAD_FOLLOWUP_LIMIT = 5
const STALE_NEW_HOURS = 48
const QUOTED_FOLLOWUP_DAYS = 5
const REMINDERS_SECTION_LIMIT = 6
const UNSCHEDULED_JOB_STALE_DAYS = 3

export default function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [metrics, setMetrics] = useState({
    totalClients: 0,
    activeQuotes: 0,
    activeJobs: 0,
    jobsToday: 0,
    needsCrew: 0,
  })

  const [quoteSnapshot, setQuoteSnapshot] = useState({
    draft: 0,
    sent: 0,
    acceptedOrConverted: 0,
    lost: 0,
  })

  const [jobSnapshot, setJobSnapshot] = useState({
    scheduledOrConfirmed: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
  })

  const [recentItems, setRecentItems] = useState([])
  const [attentionQueue, setAttentionQueue] = useState({
    needsCrewUpcoming: [],
    unscheduledJobs: [],
    staleQuotes: [],
  })
  const [todayWork, setTodayWork] = useState([])
  const [leadFollowupQueue, setLeadFollowupQueue] = useState({
    newRecent: [],
    staleNew: [],
    contactedNoQuote: [],
    quotedNeedsFollowup: [],
  })
  const [remindersQueue, setRemindersQueue] = useState({
    leads: [],
    jobs: [],
  })

  const createQuoteFromLead = useCallback(async (lead) => {
    async function ensureClientForLead(localLead) {
      if (localLead.converted_client_id) return localLead.converted_client_id

      const { data: existingClient } = await supabase
        .from('clients')
        .select('id')
        .eq('email', localLead.email)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (existingClient?.id) {
        await supabase.from('leads').update({ converted_client_id: existingClient.id }).eq('id', localLead.id)
        return existingClient.id
      }

      const { data: newClient, error: clientErr } = await supabase.from('clients').insert({
        name: localLead.name,
        email: localLead.email,
        phone: localLead.phone,
        source: 'lead-conversion',
        notes: localLead.notes,
      }).select().single()
      if (clientErr) throw clientErr

      await supabase.from('leads').update({ converted_client_id: newClient.id }).eq('id', localLead.id)
      return newClient.id
    }

    try {
      const clientId = await ensureClientForLead(lead)
      const params = new URLSearchParams({ client: clientId, lead: lead.id })
      navigate('/quotes/new?' + params.toString())
    } catch (err) {
      alert('Could not start quote flow: ' + err.message)
    }
  }, [navigate])

  useEffect(() => {
    loadDashboard()
  }, [])

  async function loadDashboard() {
    setLoading(true)
    setError('')

    try {
      const [clientsRes, quotesRes, jobsRes, leadsRes] = await Promise.all([
        supabase.from('clients').select('id', { count: 'exact', head: true }),
        supabase.from('quotes').select('id, status, created_at, quote_number').order('created_at', { ascending: false }),
        supabase.from('jobs').select('id, status, event_date, event_type, created_at, clients(name), job_staff(id)').order('created_at', { ascending: false }),
        supabase.from('leads').select('id, name, source, status, created_at, converted_client_id, email, phone, notes').order('created_at', { ascending: false }),
      ])

      const firstError = [clientsRes, quotesRes, jobsRes, leadsRes].find(r => r.error)?.error
      if (firstError) throw firstError

      const quotes = quotesRes.data || []
      const jobs = jobsRes.data || []
      const leads = leadsRes.data || []

      const activeQuotes = quotes.filter(q => !['lost', 'converted'].includes(q.status)).length
      const activeJobs = jobs.filter(j => !['completed', 'cancelled'].includes(j.status)).length
      const jobsToday = jobs.filter(j => j.event_date && isToday(new Date(j.event_date + 'T12:00:00'))).length
      const needsCrew = jobs.filter(j => {
        const assigned = Array.isArray(j.job_staff) ? j.job_staff.length : 0
        return j.status !== 'completed' && j.status !== 'cancelled' && assigned === 0
      }).length

      setMetrics({
        totalClients: clientsRes.count || 0,
        activeQuotes,
        activeJobs,
        jobsToday,
        needsCrew,
      })

      setQuoteSnapshot({
        draft: quotes.filter(q => q.status === 'draft').length,
        sent: quotes.filter(q => q.status === 'sent').length,
        acceptedOrConverted: quotes.filter(q => ['accepted', 'converted'].includes(q.status)).length,
        lost: quotes.filter(q => q.status === 'lost').length,
      })

      setJobSnapshot({
        scheduledOrConfirmed: jobs.filter(j => ['confirmed', 'staffing', 'crew_set'].includes(j.status)).length,
        inProgress: jobs.filter(j => j.status === 'in_progress').length,
        completed: jobs.filter(j => j.status === 'completed').length,
        cancelled: jobs.filter(j => j.status === 'cancelled').length,
      })

      const quoteActivity = quotes.slice(0, 3).map(q => ({
        id: q.id,
        type: 'quote',
        title: (q.quote_number || 'Quote') + ' ' + (q.status || ''),
        date: q.created_at,
        link: '/quotes/' + q.id,
      }))

      const jobActivity = jobs.slice(0, 3).map(j => ({
        id: j.id,
        type: 'job',
        title: (j.clients?.name || 'Job') + ' · ' + (j.status?.replace('_', ' ') || 'status'),
        date: j.created_at,
        link: '/jobs/' + j.id,
      }))

      setRecentItems([...quoteActivity, ...jobActivity]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 6))

      const now = new Date()
      const upcomingCutoff = addDays(now, 7)
      const staleCutoff = addDays(now, -7)

      const needsCrewUpcoming = jobs
        .filter(j => {
          if (!j.event_date) return false
          const eventDate = new Date(j.event_date + 'T12:00:00')
          const assigned = Array.isArray(j.job_staff) ? j.job_staff.length : 0
          return (
            isAfter(eventDate, now) &&
            !isAfter(eventDate, upcomingCutoff) &&
            !['completed', 'cancelled'].includes(j.status) &&
            assigned === 0
          )
        })
        .slice(0, 6)

      const unscheduledJobs = jobs
        .filter(j => !j.event_date && !['completed', 'cancelled'].includes(j.status))
        .slice(0, 6)

      const staleQuotes = quotes
        .filter(q => {
          const marker = new Date((q.created_at))
          return ['draft', 'sent'].includes(q.status) && marker < staleCutoff
        })
        .slice(0, 6)

      setAttentionQueue({ needsCrewUpcoming, unscheduledJobs, staleQuotes })

      const todayJobs = jobs
        .filter(j => j.event_date && isToday(new Date(j.event_date + 'T12:00:00')))
        .sort((a, b) => {
          const ad = new Date(a.event_date + 'T12:00:00').getTime()
          const bd = new Date(b.event_date + 'T12:00:00').getTime()
          if (ad !== bd) return ad - bd
          return (a.clients?.name || '').localeCompare(b.clients?.name || '')
        })
        .slice(0, 10)

      setTodayWork(todayJobs)

      const staleNewMs = STALE_NEW_HOURS * 60 * 60 * 1000
      const quotedCutoff = addDays(now, -QUOTED_FOLLOWUP_DAYS)
      const unscheduledJobCutoff = addDays(now, -UNSCHEDULED_JOB_STALE_DAYS)
      const actionableLeads = leads.filter(l => !['converted', 'lost'].includes(l.status))

      const newRecent = actionableLeads
        .filter(l => l.status === 'new' && (now.getTime() - new Date(l.created_at).getTime()) < staleNewMs)
        .slice(0, LEAD_FOLLOWUP_LIMIT)

      const staleNew = actionableLeads
        .filter(l => l.status === 'new' && (now.getTime() - new Date(l.created_at).getTime()) >= staleNewMs)
        .slice(0, LEAD_FOLLOWUP_LIMIT)

      const contactedNoQuote = actionableLeads
        .filter(l => l.status === 'contacted')
        .slice(0, LEAD_FOLLOWUP_LIMIT)

      const quotedNeedsFollowup = actionableLeads
        .filter(l => l.status === 'quoted' && new Date(l.created_at) <= quotedCutoff)
        .slice(0, LEAD_FOLLOWUP_LIMIT)

      setLeadFollowupQueue({ newRecent, staleNew, contactedNoQuote, quotedNeedsFollowup })

      const staleLeadReminders = actionableLeads
        .filter(l => l.status === 'new' && (now.getTime() - new Date(l.created_at).getTime()) >= staleNewMs)
        .map(l => ({
          type: 'lead',
          id: l.id,
          name: l.name,
          status: l.status,
          source: l.source,
          created_at: l.created_at,
          urgencyReason: 'Stale new lead (48h+ without first response)',
          dueTiming: `${Math.floor((now.getTime() - new Date(l.created_at).getTime()) / (1000 * 60 * 60))}h old`,
          nextAction: 'Reach out now and move status to contacted',
          openPath: '/leads/' + l.id,
          canCreateQuote: false,
        }))

      const quotedLeadReminders = actionableLeads
        .filter(l => l.status === 'quoted' && new Date(l.created_at) <= quotedCutoff)
        .map(l => ({
          type: 'lead',
          id: l.id,
          name: l.name,
          status: l.status,
          source: l.source,
          created_at: l.created_at,
          urgencyReason: `Quoted lead awaiting follow-up (${QUOTED_FOLLOWUP_DAYS}+ days)`,
          dueTiming: `${Math.floor((now.getTime() - new Date(l.created_at).getTime()) / (1000 * 60 * 60 * 24))}d since lead created`,
          nextAction: 'Follow up on quote decision',
          openPath: '/leads/' + l.id,
          canCreateQuote: false,
        }))

      const leadReminders = [...staleLeadReminders, ...quotedLeadReminders]
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .slice(0, REMINDERS_SECTION_LIMIT)

      const jobsTomorrowNeedCrew = jobs
        .filter(j => {
          if (!j.event_date) return false
          const eventDate = new Date(j.event_date + 'T12:00:00')
          const tomorrow = addDays(now, 1)
          const assigned = Array.isArray(j.job_staff) ? j.job_staff.length : 0
          return (
            eventDate.getFullYear() === tomorrow.getFullYear() &&
            eventDate.getMonth() === tomorrow.getMonth() &&
            eventDate.getDate() === tomorrow.getDate() &&
            !['completed', 'cancelled'].includes(j.status) &&
            assigned === 0
          )
        })
        .map(j => ({
          type: 'job',
          id: j.id,
          client: j.clients?.name || 'Unknown client',
          status: j.status,
          created_at: j.created_at,
          urgencyReason: 'Job is tomorrow with no crew assigned',
          dueTiming: `Due tomorrow (${fmtDate(j.event_date)})`,
          nextAction: 'Assign crew',
          openPath: '/jobs/' + j.id,
          canAssignCrew: true,
        }))

      const olderUnscheduledJobs = jobs
        .filter(j => !j.event_date && !['completed', 'cancelled'].includes(j.status) && new Date(j.created_at) <= unscheduledJobCutoff)
        .map(j => ({
          type: 'job',
          id: j.id,
          client: j.clients?.name || 'Unknown client',
          status: j.status,
          created_at: j.created_at,
          urgencyReason: `Unscheduled job older than ${UNSCHEDULED_JOB_STALE_DAYS} days`,
          dueTiming: `${Math.floor((now.getTime() - new Date(j.created_at).getTime()) / (1000 * 60 * 60 * 24))}d unscheduled`,
          nextAction: 'Set event date and staffing plan',
          openPath: '/jobs/' + j.id,
          canAssignCrew: false,
        }))

      const jobReminders = [...jobsTomorrowNeedCrew, ...olderUnscheduledJobs]
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .slice(0, REMINDERS_SECTION_LIMIT)

      setRemindersQueue({ leads: leadReminders, jobs: jobReminders })
    } catch (err) {
      console.error('Dashboard load failed:', err)
      setError(err?.message || 'Failed to load dashboard data.')
      setMetrics({ totalClients: 0, activeQuotes: 0, activeJobs: 0, jobsToday: 0, needsCrew: 0 })
      setQuoteSnapshot({ draft: 0, sent: 0, acceptedOrConverted: 0, lost: 0 })
      setJobSnapshot({ scheduledOrConfirmed: 0, inProgress: 0, completed: 0, cancelled: 0 })
      setRecentItems([])
      setAttentionQueue({ needsCrewUpcoming: [], unscheduledJobs: [], staleQuotes: [] })
      setTodayWork([])
      setLeadFollowupQueue({ newRecent: [], staleNew: [], contactedNoQuote: [], quotedNeedsFollowup: [] })
      setRemindersQueue({ leads: [], jobs: [] })
    } finally {
      setLoading(false)
    }
  }

  const attentionAllClear =
    attentionQueue.needsCrewUpcoming.length === 0 &&
    attentionQueue.unscheduledJobs.length === 0 &&
    attentionQueue.staleQuotes.length === 0

  const leadFollowupAllClear =
    leadFollowupQueue.newRecent.length === 0 &&
    leadFollowupQueue.staleNew.length === 0 &&
    leadFollowupQueue.contactedNoQuote.length === 0 &&
    leadFollowupQueue.quotedNeedsFollowup.length === 0

  const remindersAllClear =
    remindersQueue.leads.length === 0 &&
    remindersQueue.jobs.length === 0

  if (loading) return <div className='loading-state'>Loading dashboard…</div>

  return (
    <div data-testid='dashboard-page'>
      {error && (
        <div className='alert alert-error' data-testid='feedback-banner-error' style={{ marginBottom: 16 }}>
          <AlertCircle size={16} style={{ marginRight: 6 }} />
          {error}
        </div>
      )}

      <div className='page-header'>
        <div>
          <h1 className='page-title'>Dashboard</h1>
          <p className='page-subtitle'>Welcome back, {user?.full_name?.split(' ')[0]}</p>
        </div>
        <div className='text-sm text-muted'>{format(new Date(), 'EEEE, MMMM d, yyyy')}</div>
      </div>

      <div className='stats-grid' style={{ marginBottom: 'var(--sp-5)' }}>
        <Link to='/clients' data-testid='dashboard-metric-clients' className='stat-card stat-link'>
          <div className='stat-label'>Total Clients</div>
          <div className='stat-value'>{metrics.totalClients}</div>
        </Link>

        <Link to='/quotes' data-testid='dashboard-metric-active-quotes' className='stat-card stat-link'>
          <div className='stat-label'>Active Quotes</div>
          <div className='stat-value'>{metrics.activeQuotes}</div>
        </Link>

        <Link to='/jobs' data-testid='dashboard-metric-active-jobs' className='stat-card stat-link'>
          <div className='stat-label'>Active Jobs</div>
          <div className='stat-value'>{metrics.activeJobs}</div>
        </Link>

        <Link to='/jobs' data-testid='dashboard-metric-jobs-today' className='stat-card stat-link'>
          <div className='stat-label'>Jobs Today</div>
          <div className='stat-value'>{metrics.jobsToday}</div>
        </Link>

        <Link to='/jobs' data-testid='dashboard-metric-needs-crew' className='stat-card stat-link' style={{ borderColor: 'var(--color-amber)', background: '#fffaf0' }}>
          <div className='stat-label'>Jobs Needing Crew</div>
          <div className='stat-value' style={{ color: 'var(--color-amber)' }}>{metrics.needsCrew}</div>
        </Link>
      </div>

      <div className='card' data-testid='lead-followup-queue' style={{ marginBottom: 'var(--sp-5)' }}>
        <div className='card-header'>
          <h3 style={{ fontWeight: 600 }}>Lead Follow-Up Queue</h3>
          <Link to='/leads' className='btn btn-ghost btn-sm'>Open Leads</Link>
        </div>
        {leadFollowupAllClear && (
          <div style={{ padding: '0 20px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            No leads require follow-up right now.
          </div>
        )}
        <div className='card-body' style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div data-testid='lead-followup-section-new' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8 }}>New leads (&lt; 48h)</div>
            {leadFollowupQueue.newRecent.length === 0 ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>No recent new leads.</div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {leadFollowupQueue.newRecent.map(lead => (
                  <div key={lead.id} data-testid={'lead-followup-item-new-' + lead.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{lead.name}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{lead.source || '—'} · {fmtDate(lead.created_at)} · {lead.status}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Next: contact and set status to contacted</div>
                    </div>
                    <Link to={'/leads/' + lead.id} className='btn btn-ghost btn-sm'>Open</Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid='lead-followup-section-stale' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8, color: 'var(--color-amber)' }}>Stale new leads (48h+)</div>
            {leadFollowupQueue.staleNew.length === 0 ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>No stale new leads.</div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {leadFollowupQueue.staleNew.map(lead => (
                  <div key={lead.id} data-testid={'lead-followup-item-stale-' + lead.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{lead.name}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{lead.source || '—'} · {fmtDate(lead.created_at)} · {lead.status}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Next: urgent outreach and triage</div>
                    </div>
                    <Link to={'/leads/' + lead.id} className='btn btn-ghost btn-sm'>Open</Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid='lead-followup-section-contacted' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8 }}>Contacted leads (no quote yet)</div>
            {leadFollowupQueue.contactedNoQuote.length === 0 ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>No contacted leads awaiting quote.</div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {leadFollowupQueue.contactedNoQuote.map(lead => (
                  <div key={lead.id} data-testid={'lead-followup-item-contacted-' + lead.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{lead.name}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{lead.source || '—'} · {fmtDate(lead.created_at)} · {lead.status}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Next: create and send quote</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className='btn btn-secondary btn-sm' onClick={() => createQuoteFromLead(lead)}>
                        <FileText size={13} /> Create Quote
                      </button>
                      <Link to={'/leads/' + lead.id} className='btn btn-ghost btn-sm'>Open</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid='lead-followup-section-quoted' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8 }}>Quoted leads needing follow-up (5+ days)</div>
            {leadFollowupQueue.quotedNeedsFollowup.length === 0 ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>No quoted follow-up items.</div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {leadFollowupQueue.quotedNeedsFollowup.map(lead => (
                  <div key={lead.id} data-testid={'lead-followup-item-quoted-' + lead.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{lead.name}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{lead.source || '—'} · {fmtDate(lead.created_at)} · {lead.status}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Next: follow up on quote decision</div>
                    </div>
                    <Link to={'/leads/' + lead.id} className='btn btn-ghost btn-sm'>Open</Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='card' data-testid='reminders-queue' style={{ marginBottom: 'var(--sp-5)' }}>
        <div className='card-header'>
          <h3 style={{ fontWeight: 600 }}>Reminders / Alerts</h3>
        </div>
        {remindersAllClear && (
          <div style={{ padding: '0 20px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            No urgent reminders right now.
          </div>
        )}
        <div className='card-body' style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div data-testid='reminders-section-leads' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8 }}>Lead alerts</div>
            {remindersQueue.leads.length === 0 ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>No urgent lead reminders.</div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {remindersQueue.leads.map(item => (
                  <div key={item.id} data-testid={'reminder-item-lead-' + item.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{item.name}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                        lead · {item.source || '—'} · {item.status} · {item.dueTiming}
                      </div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-amber)' }}>Urgency: {item.urgencyReason}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Next: {item.nextAction}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {item.canCreateQuote && (
                        <button className='btn btn-secondary btn-sm' onClick={() => createQuoteFromLead(item)}>
                          <FileText size={13} /> Create Quote
                        </button>
                      )}
                      <Link to={item.openPath} className='btn btn-ghost btn-sm'>Open</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid='reminders-section-jobs' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8 }}>Job alerts</div>
            {remindersQueue.jobs.length === 0 ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>No urgent job reminders.</div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {remindersQueue.jobs.map(item => (
                  <div key={item.id} data-testid={'reminder-item-job-' + item.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{item.client}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                        job · {item.status?.replace('_', ' ')} · {item.dueTiming}
                      </div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-amber)' }}>Urgency: {item.urgencyReason}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Next: {item.nextAction}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {item.canAssignCrew && (
                        <Link to={item.openPath} className='btn btn-secondary btn-sm'>Assign Crew</Link>
                      )}
                      <Link to={item.openPath} className='btn btn-ghost btn-sm'>Open</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='card' data-testid='dashboard-attention-queue' style={{ marginBottom: 'var(--sp-5)' }}>
        <div className='card-header'>
          <h3 style={{ fontWeight: 600 }}>Attention Queue</h3>
        </div>
        {attentionAllClear && (
          <div data-testid='empty-state-dashboard-attention' style={{ padding: '0 20px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            No attention items right now.
          </div>
        )}
        <div className='card-body' style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div data-testid='dashboard-attention-section-needs-crew' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8 }}>Upcoming jobs needing crew</div>
            {attentionQueue.needsCrewUpcoming.length === 0 ? (
              <div data-testid='empty-state-dashboard-attention-needs-crew' style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                No upcoming jobs need crew right now.
              </div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {attentionQueue.needsCrewUpcoming.map(j => (
                  <div key={j.id} data-testid={'dashboard-attention-item-needs-crew-' + j.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{j.clients?.name || 'Unknown client'}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{j.status?.replace('_', ' ')} · {fmtDate(j.event_date)}</div>
                    </div>
                    <Link to={'/jobs/' + j.id} data-testid={'dashboard-attention-link-needs-crew-' + j.id} className='btn btn-ghost btn-sm'>Open</Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid='dashboard-attention-section-unscheduled' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8 }}>Unscheduled jobs</div>
            {attentionQueue.unscheduledJobs.length === 0 ? (
              <div data-testid='empty-state-dashboard-attention-unscheduled' style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                No unscheduled jobs right now.
              </div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {attentionQueue.unscheduledJobs.map(j => (
                  <div key={j.id} data-testid={'dashboard-attention-item-unscheduled-' + j.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{j.clients?.name || 'Unknown client'}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{j.status?.replace('_', ' ')} · no event date</div>
                    </div>
                    <Link to={'/jobs/' + j.id} data-testid={'dashboard-attention-link-unscheduled-' + j.id} className='btn btn-ghost btn-sm'>Open</Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid='dashboard-attention-section-stale-quotes' className='stat-card' style={{ padding: 10 }}>
            <div className='stat-label' style={{ marginBottom: 8 }}>Stale quotes (7+ days)</div>
            {attentionQueue.staleQuotes.length === 0 ? (
              <div data-testid='empty-state-dashboard-attention-stale-quotes' style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                No stale quotes need follow-up.
              </div>
            ) : (
              <div className='job-list' style={{ gap: 6 }}>
                {attentionQueue.staleQuotes.map(q => (
                  <div key={q.id} data-testid={'dashboard-attention-item-stale-quotes-' + q.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{q.quote_number || 'Quote'}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{q.status} · {fmtDate(q.created_at)}</div>
                    </div>
                    <Link to={'/quotes/' + q.id} data-testid={'dashboard-attention-link-stale-quotes-' + q.id} className='btn btn-ghost btn-sm'>Open</Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='card' data-testid='dashboard-today-work' style={{ marginBottom: 'var(--sp-5)' }}>
        <div className='card-header'>
          <div className='flex items-center gap-2'>
            <CalendarDays size={16} />
            <h3 style={{ fontWeight: 600 }}>Today’s Work</h3>
          </div>
          <div style={{ display: 'flex', gap: 8, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            <span>{todayWork.length} today</span>
            <span>{todayWork.filter(j => (Array.isArray(j.job_staff) ? j.job_staff.length : 0) === 0 && !['completed', 'cancelled'].includes(j.status)).length} need crew</span>
            <span>{todayWork.filter(j => j.status === 'in_progress').length} in progress</span>
          </div>
        </div>
        {todayWork.length === 0 ? (
          <div className='empty-state' data-testid='empty-state-dashboard-today-work' style={{ padding: 24 }}>
            <p style={{ fontWeight: 600 }}>No jobs scheduled for today</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>You’re clear for now—check the schedule for upcoming work.</p>
            <div style={{ marginTop: 10 }}>
              <Link to='/jobs' className='btn btn-secondary btn-sm'>Open Schedule</Link>
            </div>
          </div>
        ) : (
          <div className='job-list'>
            {todayWork.map(job => {
              const assignedCount = Array.isArray(job.job_staff) ? job.job_staff.length : 0
              const hasCrew = assignedCount > 0
              const inProgress = job.status === 'in_progress'
              const completed = job.status === 'completed'

              return (
                <div key={job.id} data-testid={'dashboard-today-work-item-' + job.id} className='job-list-item' style={{ justifyContent: 'space-between' }}>
                  <div>
                    <div className='job-list-client'>{job.clients?.name || 'Unknown client'}</div>
                    <div className='job-list-meta' style={{ textTransform: 'capitalize' }}>{job.event_type || 'Job'} · {job.status?.replace('_', ' ')}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {!completed && !hasCrew && <span data-testid={'dashboard-today-work-badge-needs-crew-' + job.id} className='badge badge-staffing'>Needs crew</span>}
                    {hasCrew && <span data-testid={'dashboard-today-work-badge-crew-assigned-' + job.id} className='badge badge-crew_set'>Crew assigned</span>}
                    {inProgress && <span data-testid={'dashboard-today-work-badge-in-progress-' + job.id} className='badge badge-in_progress'>In progress</span>}
                    {completed && <span data-testid={'dashboard-today-work-badge-completed-' + job.id} className='badge badge-completed'>Completed</span>}
                    <Link to={'/jobs/' + job.id} data-testid={'dashboard-today-work-link-' + job.id} className='btn btn-ghost btn-sm'>Open</Link>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className='dashboard-grid'>
        <div className='card' data-testid='dashboard-quotes-section'>
          <div className='card-header'>
            <div className='flex items-center gap-2'>
              <FileText size={16} />
              <h3 style={{ fontWeight: 600 }}>Quote Pipeline Snapshot</h3>
            </div>
            <Link to='/quotes' className='btn btn-ghost btn-sm'>Open Quotes</Link>
          </div>
          <div className='card-body' style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
            <div className='stat-card' style={{ padding: 10 }}><div className='stat-label'>Draft</div><div className='stat-value' style={{ fontSize: 'var(--text-xl)' }}>{quoteSnapshot.draft}</div></div>
            <div className='stat-card' style={{ padding: 10 }}><div className='stat-label'>Sent</div><div className='stat-value' style={{ fontSize: 'var(--text-xl)' }}>{quoteSnapshot.sent}</div></div>
            <div className='stat-card' style={{ padding: 10 }}><div className='stat-label'>Accepted / Converted</div><div className='stat-value' style={{ fontSize: 'var(--text-xl)' }}>{quoteSnapshot.acceptedOrConverted}</div></div>
            <div className='stat-card' style={{ padding: 10 }}><div className='stat-label'>Lost</div><div className='stat-value' style={{ fontSize: 'var(--text-xl)' }}>{quoteSnapshot.lost}</div></div>
          </div>
        </div>

        <div className='card' data-testid='dashboard-jobs-section'>
          <div className='card-header'>
            <div className='flex items-center gap-2'>
              <Briefcase size={16} />
              <h3 style={{ fontWeight: 600 }}>Job Status Snapshot</h3>
            </div>
            <Link to='/jobs' className='btn btn-ghost btn-sm'>Open Schedule</Link>
          </div>
          <div className='card-body' style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
            <div className='stat-card' style={{ padding: 10 }}><div className='stat-label'>Scheduled / Confirmed</div><div className='stat-value' style={{ fontSize: 'var(--text-xl)' }}>{jobSnapshot.scheduledOrConfirmed}</div></div>
            <div className='stat-card' style={{ padding: 10 }}><div className='stat-label'>In Progress</div><div className='stat-value' style={{ fontSize: 'var(--text-xl)' }}>{jobSnapshot.inProgress}</div></div>
            <div className='stat-card' style={{ padding: 10 }}><div className='stat-label'>Completed</div><div className='stat-value' style={{ fontSize: 'var(--text-xl)' }}>{jobSnapshot.completed}</div></div>
            <div className='stat-card' style={{ padding: 10 }}><div className='stat-label'>Cancelled</div><div className='stat-value' style={{ fontSize: 'var(--text-xl)' }}>{jobSnapshot.cancelled}</div></div>
          </div>
        </div>

        <div className='card' data-testid='dashboard-quick-actions'>
          <div className='card-header'>
            <h3 style={{ fontWeight: 600 }}>Quick Actions</h3>
          </div>
          <div className='card-body' style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Link to='/clients' className='btn btn-secondary btn-sm'><Users size={14} /> Create Client</Link>
            <Link to='/quotes/new' className='btn btn-secondary btn-sm'><Plus size={14} /> Create Quote</Link>
            <Link to='/jobs' className='btn btn-ghost btn-sm'><CalendarDays size={14} /> Open Schedule</Link>
            <Link to='/jobs' className='btn btn-ghost btn-sm'><Briefcase size={14} /> View Active Jobs</Link>
          </div>
        </div>

        <div className='card'>
          <div className='card-header'>
            <h3 style={{ fontWeight: 600 }}>Recent Activity</h3>
          </div>
          {recentItems.length === 0 ? (
            <div className='empty-state' data-testid='empty-state-dashboard-recent-activity' style={{ padding: 24 }}>
              <p style={{ fontWeight: 600 }}>No recent activity yet</p>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Recent quote and job updates will appear here.</p>
            </div>
          ) : (
            <div className='job-list'>
              {recentItems.map(item => (
                <Link key={item.type + '-' + item.id} to={item.link} className='job-list-item'>
                  <div>
                    <div className='job-list-client'>{item.title}</div>
                    <div className='job-list-meta' style={{ textTransform: 'capitalize' }}>{item.type}</div>
                  </div>
                  <div className='job-list-date'>{fmtDate(item.date)}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .stat-link {
          text-decoration: none;
          transition: box-shadow 0.15s ease, transform 0.1s ease;
        }
        .stat-link:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-1px);
        }
        .dashboard-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--sp-5);
        }
        .job-list { display: flex; flex-direction: column; }
        .job-list-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          border-bottom: 1px solid var(--color-border);
          text-decoration: none;
          color: var(--color-text);
          gap: 12px;
          transition: background 0.1s ease;
        }
        .job-list-item:last-child { border-bottom: none; }
        .job-list-item:hover { background: var(--color-bg); }
        .job-list-client { font-size: var(--text-sm); font-weight: 600; }
        .job-list-meta { font-size: var(--text-xs); color: var(--color-text-muted); margin-top: 2px; }
        .job-list-date { font-size: var(--text-xs); color: var(--color-text-secondary); font-weight: 500; }
        .loading-state { padding: 60px; text-align: center; color: var(--color-text-muted); }
        @media (max-width: 900px) {
          .dashboard-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  )
}
